import React from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import { CheckRounded } from '@mui/icons-material';
import { useShellTranslation } from './useShellTranslation';
import { shellTokens } from './shellTokens';
import { useShell } from './ShellContext';
import { useLocale } from '../../contexts/LocaleContext';
import { SupportedLanguage, SupportedRegion } from '../../i18n';
import { buildRegionSwitchUrl } from '../../utils/region';
import { useOverlayFocus } from './useOverlayFocus';

const { color, radius, shadow, z } = shellTokens;

const REGION_FLAGS: Record<SupportedRegion, string> = { US: '🇺🇸', CA: '🇨🇦' };

/**
 * Two independent axes: region is a property of the *data* (it sets currency),
 * language is a property of the *user*. French exists only in the Canadian
 * market, so it renders disabled rather than hidden — the dependency should be
 * visible, not mysterious.
 */
const LANGUAGES_BY_REGION: Record<SupportedRegion, SupportedLanguage[]> = {
  US: ['en'],
  CA: ['en', 'fr'],
};

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    sx={{
      p: '8px 10px 4px',
      fontSize: '10.5px',
      fontWeight: 700,
      letterSpacing: '.08em',
      textTransform: 'uppercase',
      color: color.faint,
    }}
  >
    {children}
  </Typography>
);

export const RegionMenu: React.FC = () => {
  const { t } = useShellTranslation();
  const { overlay, closeOverlay } = useShell();
  const {
    language,
    region,
    currency,
    setLanguage,
    setRegion,
    supportedLanguages,
    supportedRegions,
  } = useLocale();
  const { containerRef, onKeyDown } = useOverlayFocus<HTMLDivElement>(overlay?.kind === 'region', {
    menu: true,
  });

  if (overlay?.kind !== 'region') return null;

  const availableLanguages = LANGUAGES_BY_REGION[region];

  const handleRegionChange = (next: SupportedRegion) => {
    // Falling back to English keeps a French UI from surviving into a market
    // that has no French copy.
    const nextLanguage = LANGUAGES_BY_REGION[next].includes(language) ? language : 'en';
    if (nextLanguage !== language) setLanguage(nextLanguage);
    setRegion(next);

    const switchUrl = buildRegionSwitchUrl(next === 'CA' ? 'ca' : 'us', nextLanguage);
    if (switchUrl) {
      window.location.href = switchUrl;
      return;
    }
    closeOverlay();
  };

  const row = (
    key: string,
    title: string,
    subtitle: string,
    checked: boolean,
    disabled: boolean,
    onClick: () => void,
    flag?: string,
  ) => (
    <ButtonBase
      key={key}
      role="menuitemradio"
      aria-checked={checked}
      aria-disabled={disabled}
      // Roving tabindex: the hook moves focus with the arrow keys.
      tabIndex={-1}
      disabled={disabled}
      onClick={onClick}
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        p: '7px 10px',
        borderRadius: radius.menuRow,
        textAlign: 'left',
        cursor: disabled ? 'not-allowed' : 'pointer',
        pointerEvents: disabled ? 'auto' : undefined,
        background: checked ? color.accentTint : 'transparent',
        '&:hover': { background: disabled ? 'transparent' : color.lineSubtle },
      }}
    >
      {flag && (
        <Box component="span" sx={{ fontSize: '15px', lineHeight: 1, flexShrink: 0 }}>
          {flag}
        </Box>
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          noWrap
          sx={{
            fontSize: '13px',
            fontWeight: 600,
            color: disabled ? color.disabled : checked ? color.accentDeep : color.ink,
          }}
        >
          {title}
        </Typography>
        <Typography
          noWrap
          sx={{ fontSize: '11.5px', color: disabled ? color.disabled : color.muted }}
        >
          {subtitle}
        </Typography>
      </Box>
      {checked && <CheckRounded sx={{ fontSize: 15, color: color.accent, flexShrink: 0 }} />}
    </ButtonBase>
  );

  return (
    <>
      <Box
        onClick={closeOverlay}
        sx={{ position: 'fixed', inset: 0, zIndex: z.menu - 1 }}
        aria-hidden
      />
      <Box
        ref={containerRef}
        role="menu"
        tabIndex={-1}
        onKeyDown={onKeyDown}
        aria-label={t('shell.region.tooltip')}
        sx={{
          position: 'fixed',
          top: overlay.at.top,
          right: overlay.at.right,
          width: 262,
          maxWidth: 'calc(100vw - 24px)',
          // Anchored below the header chip; a phone in landscape leaves less
          // room than the two sections plus summary need.
          maxHeight: `calc(100vh - ${overlay.at.top + 12}px)`,
          overflowY: 'auto',
          zIndex: z.menu,
          background: color.white,
          border: `1px solid ${color.line}`,
          borderRadius: radius.menu,
          boxShadow: shadow.menu,
          p: '6px',
        }}
      >
        <SectionLabel>{t('shell.region.language')}</SectionLabel>
        {(Object.keys(supportedLanguages) as SupportedLanguage[]).map((code) => {
          const unavailable = !availableLanguages.includes(code);
          return row(
            code,
            supportedLanguages[code].nativeName,
            `${code}-${region}`,
            language === code,
            unavailable,
            () => setLanguage(code),
          );
        })}

        <Typography sx={{ p: '4px 10px 8px', fontSize: '11.5px', color: color.faint }}>
          {t('shell.region.languageNote')}
        </Typography>

        <SectionLabel>{t('shell.region.region')}</SectionLabel>
        {(Object.keys(supportedRegions) as SupportedRegion[]).map((code) =>
          row(
            code,
            t(`shell.region.name.${code}`),
            supportedRegions[code].currency,
            region === code,
            false,
            () => handleRegionChange(code),
            REGION_FLAGS[code],
          ),
        )}

        <Typography
          sx={{
            mt: '4px',
            pt: '8px',
            px: '10px',
            pb: '2px',
            borderTop: `1px solid ${color.lineSubtle}`,
            fontSize: '11.5px',
            color: color.muted,
          }}
        >
          {t('shell.region.summary', {
            language: supportedLanguages[language].nativeName,
            region: t(`shell.region.name.${region}`),
            currency,
          })}
        </Typography>
      </Box>
    </>
  );
};
